import { Button } from "@component-ui/utility/Button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@component-ui/utility/Dialog";
import { Input } from "@component-ui/utility/Input";
import { Label } from "@component-ui/utility/Label";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@component-ui/utility/Tooltip";
import { FORM_KEY } from "@constant/form";
import useUpdateDocumentMutate from "@hooks/document/useUpdateDocumentMutate";
import { Loader2, Pencil } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { FC, FormEvent, ReactElement, useEffect, useState } from "react";
import { Document } from "types/backend/document";

type UpdateDocumentDialogProps = {
    document: Document;
    ButtonElement?: ReactElement;
    onClose?: () => void;
};

const UpdateDocumentDialogCapture: FC = () => {
    const posthog = usePostHog();
    useEffect(() => {
        posthog?.capture("$pageview", { pageName: "/document/edit" });
    }, []);

    return null;
};

const UpdateDocumentDialog: FC<UpdateDocumentDialogProps> = ({
    document,
    ButtonElement = (
        <Button variant={"ghost"} size={"icon"}>
            <Pencil className="w-4 h-4" />
        </Button>
    ),
    onClose,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const { mutate: updateDocument, isPending: isUpdating } =
        useUpdateDocumentMutate(document.id);

    function handleSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        updateDocument(formData, {
            onSuccess: () => {
                setIsOpen(false);
            },
        });
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(e) => {
                setIsOpen(e);
                if (!e && onClose) {
                    onClose();
                }
            }}
        >
            <TooltipProvider delayDuration={200}>
                <Tooltip>
                    <DialogTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <TooltipTrigger asChild>{ButtonElement}</TooltipTrigger>
                    </DialogTrigger>
                    <TooltipContent side="bottom">Update name</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Update document</DialogTitle>
                    <UpdateDocumentDialogCapture />
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <Label htmlFor={FORM_KEY.DOCUMENT.TITLE}>
                        Document title
                    </Label>
                    <Input
                        name={FORM_KEY.DOCUMENT.TITLE}
                        defaultValue={document.title}
                    />
                    <Button
                        type="submit"
                        disabled={isUpdating}
                        className="w-full"
                    >
                        {isUpdating && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Update
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default UpdateDocumentDialog;
